import type { Config } from "tailwindcss";
import plugin from "tailwindcss/plugin";
import TailwindCssFormsPlugin from "@tailwindcss/forms";

const customColors = {
  primary: {
    // Blue
    DEFAULT: "#0E0549",
    25: "#AEAEFF10",
    50: "#F2F3FF",
    100: "#E7E7FF",
    200: "#D2D4FF",
    300: "#AEAEFF",
    400: "#8580FF",
    500: "#5E4DFF",
    600: "#4828FF",
    700: "#3A16EB",
    800: "#3112C5",
    900: "#2911A1",
  },
  secondary: {
    // Peche
    DEFAULT: "#440C06",
    50: "#FFF5ED",
    100: "#FFE8D4",
    200: "#FFCCA9",
    300: "#FFAB76",
    400: "#FE7A39",
    500: "#FC5513",
    600: "#ED3B09",
    700: "#C52909",
    800: "#9C2210",
    900: "#7E1E10",
  },
  tertiary: {
    //Vert
    DEFAULT: "#5CE5CE",
    light: "#E1FAF4",
    50: "#F0FDFA",
    100: "#AFFAED",
    200: "#9FF5E6",
    300: "#90F0E0",
    400: "#7AEBD8",
    500: "#32CFB9",
  },
  gris: {
    // Gris
    DEFAULT: "#16151e",
    50: "#fafafa",
    100: "#f3f3f6",
    200: "#e2e1ea",
    300: "#d1d0dc",
    400: "#9b98b3",
    500: "#696680",
    600: "#4c4964",
    700: "#3a384d",
    800: "#25242d",
    900: "#16151e",
  },
  error: {
    DEFAULT: "#FFAB76",
  },
  graph: {
    revenue: {
      reel: "#EDEEF2",
    },
    expenses: {
      reel: "#E5E2E1",
    },
    social: {
      reel: "#F7F4F2",
    },
  },
  textColor: "#1C1B26",
  notif:"#FF7B6F",
  bgnotif:"#FFF0EE",
  customWhite: "#FFFDFC",
  customGray: "#696680",
  customLightGray: "#F4F4F6",
  generalBg: "#F9F9F9",
  textInfo: "#696680",
  selection: "#f3f3f6",
  info: "#F3F3F6",
  border: "#E2E1EA",
  inputFocusBorder: "#696680",
  selectInputFocusBorder: "#9592a6",
  link: "#FFAB76",
  danger: "#FF4B67",
  warning: "#FF9500",
  success: "#60B527",
  custom: {
    50: "#FFFCFA59",
    45: "#FFFCFA36",
  },
};

const customBackgroundImage = {
  "custom-gradient":
    "linear-gradient(209.01deg, rgba(255, 171, 118, 0.3) 14.22%, rgba(14, 5, 73, 0.3) 82.41%)",
  "modal-orange-gradient":
    "linear-gradient(to right, #FE7A39 80%, #FF9C63 93.93%, #FFCCA9 100%, #FE7A39 90%)",
  "navbar-linear-gradient":
    "linear-gradient(to right, #09032F, #12065F, #09032F)",
};

const config: Config = {
  content: [
    "./src/routes/**/*.tsx",
    "./src/lib/components/**/*.tsx",
    "./src/features/**/*.tsx",
  ],
  theme: {
    extend: {
      flexWrap: {
        unset: "unset",
      },
      screens: {
        // Par exemple : breakpoint à 900px
        screen850: "850px",
      },
      fontFamily: {
        figtree: ["Figtree", "Arial", "sans-serif"],
      },
      colors: customColors,
      borderColor: {
        DEFAULT: customColors.border,
      },
      translate: {
        "3d": "translate3d(0,0,0)",
      },
      fontSize: {
        tiny: "10px",
        "2xs": "11px",
      },
      backgroundImage: customBackgroundImage,
      keyframes: {
        popup: {
          "0%": {
            opacity: "0",
            scale: "0.85",
          },
          "100%": {
            opacity: "1",
            scale: "1",
          },
        },
        fadeIn: {
          "0%": { opacity: "0" },
          "100%": { opacity: "1" },
        },
        fadeOut: {
          "0%": { opacity: "1" },
          "100%": { opacity: "0" },
        },
        showModalContent: {
          "0%": { opacity: "0", transform: "translateY(20px) scale(0.95)" },
          "100%": { opacity: "1", transform: "translateY(0) scale(1)" },
        },
        hideModalContent: {
          "0%": { opacity: "1", transform: "translateY(0) scale(1)" },
          "100%": { opacity: "0", transform: "translateY(20px) scale(0.95)" },
        },
        "spin-reverse": {
          from: { transform: "rotate(360deg)" },
          to: { transform: "rotate(0deg)" },
        },
        toastProgress: {
          "0%": { width: "100%" },
          "100%": { width: "0%" },
        },
        dropdownIn: {
          "0%": { opacity: "0", scale: "1 0.85" },
          "100%": { opacity: "1", scale: "1 1" },
        },
        dropdownOut: {
          "0%": { opacity: "1", scale: "1 1" },
          "100%": { opacity: "0", scale: "1 0.85" },
        },
      },
      animation: {
        popup: "popup 0.15s ease-in-out",
        fadeIn: "fadeIn 0.2s ease-in-out",
        fadeOut: "fadeOut 0.2s ease-in-out",
        showModalContent: `showModalContent 0.3s ease`,
        hideModalContent: "hideModalContent 0.3s ease",
        "spin-reverse": "spin-reverse 1s linear infinite",
        "toast-progress": "toastProgress 4s linear forwards",
        dropdownIn: "dropdownIn 0.2s ease-out",
        dropdownOut: "dropdownOut 0.2s ease-in forwards",
      },
      borderRadius: {
        pill: "100vh",
        circle: "50%",
        inherit: "inherit",
      },
      boxShadow: {
        // 80% of the default shadow-sm (0 1px 2px 0 rgb(0 0 0 / 0.05))
        xs: "0 0.9px 1.7px 0 rgb(0 0 0 / 0.045)",
      },
      gridTemplateColumns: {
        16: "repeat(16, auto)",
        15: "repeat(15, auto)",
        14: "repeat(14, minmax(0, 1fr))",
        13: "repeat(13, minmax(0, 1fr))",
      },
    },
  },
  plugins: [
    TailwindCssFormsPlugin,
    plugin(({ addVariant }) => {
      addVariant("aria-invalid", '&[aria-invalid="true"]');
    }),
    plugin(({ addUtilities, theme }) => {
      const durations = theme("transitionDuration") as Record<string, string>;
      const utilities = Object.keys(durations).reduce(
        (acc, key) => {
          acc[`.animation-duration-${key}`] = {
            animationDuration: durations[key] as string,
          };
          return acc;
        },
        {} as Record<string, Record<string, string>>,
      );
      addUtilities(utilities);
    }),
  ],
};

export { customColors };

export default config;
